import type { IOwner } from '@app/land-hold/owner/models/Owner';
import type { ISteward } from '@app/land-hold/steward/models/Steward';
import type { IState } from '@app/location/state/models/State';
import { type IProperty, Property } from '@app/land-hold/property/models/Property.ts';
import type { IInventoryView } from '@app/land-hold/inventory/models/InventoryView';
import type { IInventoryType } from '@app/land-hold/inventory-type/models/InventoryType';
import type { MultiObjectInclude, SingleObjectInclude, Include } from '@shared/types/ApiResponse';
import type { CreateRoute, DestroyRoute, EditRoute, IndexRoute, ShowRoute, StoreRoute, UpdateRoute } from '@shared/types/Route';
import type { IProject } from '@app/land-hold/project/models/Project';

export const AvailableIncludes = {
    Owner: 'owner',
    Steward: 'steward',
    State: 'state',
    InventoriesView: 'inventories_view',
    InventoriesByYear: 'inventories_by_year',
    InventoryType: 'inventory_type',
    Projects: 'projects',
    Versions: 'versions',
} as const;

type InventoryTypeInclude = Include<typeof AvailableIncludes, 'inventory_type', IInventoryType, SingleObjectInclude<IInventoryType>>;
type OwnerInclude = Include<typeof AvailableIncludes, 'owner', IOwner, SingleObjectInclude<IOwner>>;
type StewardInclude = Include<typeof AvailableIncludes, 'steward', ISteward, SingleObjectInclude<ISteward>>;
type StateInclude = Include<typeof AvailableIncludes, 'state', IState, SingleObjectInclude<IState>>;
export type InventoriesViewInclude = Include<typeof AvailableIncludes, 'inventories_view', IInventoryView, MultiObjectInclude<IInventoryView>>;
export type ProjectsInclude = Include<typeof AvailableIncludes, 'projects', IProject, MultiObjectInclude<IProject>>;
export type Year = Record<'year', number>;
type InventoriesByYearInclude = Include<typeof AvailableIncludes, 'inventories_by_year', Year, MultiObjectInclude<Year>>;

export type IPropertyDefaultIncludes = InventoriesByYearInclude;
type IPropertyWithDefaultIncludes = IProperty & IPropertyDefaultIncludes;
export type AllPropertyIncludes = IPropertyWithDefaultIncludes & InventoryTypeInclude & OwnerInclude & StewardInclude & StateInclude & InventoriesViewInclude & ProjectsInclude;
export type IndexPropertyIncludes = IPropertyWithDefaultIncludes & InventoryTypeInclude & OwnerInclude & StewardInclude & StateInclude & ProjectsInclude;
export type ShowPropertyIncludes = IndexPropertyIncludes;

export function routes(): {
    index: IndexRoute<'properties.index', IndexPropertyIncludes>;
    show: ShowRoute<'properties.show', ShowPropertyIncludes>;
    destroy: DestroyRoute<'properties.destroy'>;
    create: CreateRoute<'properties.create'>;
    store: StoreRoute<'properties.store'>;
    edit: EditRoute<'properties.edit'>;
    update: UpdateRoute<'properties.update'>;
    updateInventoryGenerationPause: UpdateRoute<'properties.inventory-generation-pause.update'>;
    versions: {
        store: StoreRoute<'properties.versions.store'>;
        update: UpdateRoute<'properties.versions.update'>;
        updateReason: UpdateRoute<'properties.versions.update_reason'>;
        destroy: DestroyRoute<'properties.versions.destroy'>;
    };
    availableVersionYears: {
        index: ShowRoute<'properties.available_version_years.index'>;
    };
} {
    return {
        index: { method: 'get', name: 'properties.index', params: { include: [AvailableIncludes.InventoryType, AvailableIncludes.Owner, AvailableIncludes.Steward, AvailableIncludes.State, AvailableIncludes.Projects], orderBy: 'updated_at', sortedBy: 'desc' }, datatableHeaders: () => Property.headers(), page: usePage() },
        show: { method: 'get', name: 'properties.show', params: { include: [AvailableIncludes.InventoryType, AvailableIncludes.Owner, AvailableIncludes.Steward, AvailableIncludes.State, AvailableIncludes.Projects] }, page: usePage() },
        destroy: { method: 'delete', name: 'properties.destroy', page: usePage() },
        create: { method: 'get', name: 'properties.create', page: usePage() },
        store: { method: 'post', name: 'properties.store', page: usePage() },
        edit: { method: 'get', name: 'properties.edit', page: usePage() },
        update: { method: 'patch', name: 'properties.update', page: usePage() },
        updateInventoryGenerationPause: { method: 'patch', name: 'properties.inventory-generation-pause.update', page: usePage() },
        versions: {
            store: { method: 'post', name: 'properties.versions.store', page: usePage() },
            update: { method: 'patch', name: 'properties.versions.update', page: usePage() },
            updateReason: { method: 'patch', name: 'properties.versions.update_reason', page: usePage() },
            destroy: { method: 'delete', name: 'properties.versions.destroy', page: usePage() },
        },
        availableVersionYears: {
            index: { method: 'get', name: 'properties.available_version_years.index', page: usePage() },
        },
    };
}
